import React, { useState, useEffect, useRef } from 'react'
import { Fragment } from 'react'
import { Disclosure, Menu, Transition } from '@headlessui/react'
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  KeyIcon,
  HeartIcon,
} from '@heroicons/react/24/outline'
import {
  HomeIcon,
  ArchiveBoxIcon,
  DocumentIcon,
  UsersIcon,
  CloudArrowUpIcon,
  HeartIcon as HeartSolidIcon,
} from '@heroicons/react/24/solid'
import { useAuth } from '../contexts/AuthContext'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import VersionInfo from './VersionInfo'
import CookieIconLogo from '../assets/CookieIconLogo.jpeg'
import DashboardBG from '../assets/DashboardBG.jpeg'
import DashboardBG1 from '../assets/DashboardBG1.jpeg'
import DashboardBG2 from '../assets/DashboardBG2.jpeg'
import DashboardBG3 from '../assets/DashboardBG3.jpeg'
import DashboardBG4 from '../assets/DashboardBG4.jpeg'
import DashboardBG5 from '../assets/DashboardBG5.jpeg'

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Inventory', href: '/inventory', icon: HeartIcon },
  { name: 'Storage Bins', href: '/storage-bins', icon: ArchiveBoxIcon },
  { name: 'Bulk Processing', href: '/bulk-processing', icon: CloudArrowUpIcon },
  { name: 'Sharing', href: '/sharing', icon: UsersIcon },
  { name: 'Measurement Sheet', href: '/measurement-sheet', icon: DocumentIcon },
]

// Background images array for rotation
const backgroundImages = [
  DashboardBG,
  DashboardBG1,
  DashboardBG2,
  DashboardBG3,
  DashboardBG4,
  DashboardBG5,
]

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { user, profile, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  
  // Background rotation state
  const [currentBackgroundIndex, setCurrentBackgroundIndex] = useState(0)
  const currentBackgroundRef = useRef(0)
  
  // Update ref when state changes
  useEffect(() => {
    currentBackgroundRef.current = currentBackgroundIndex
  }, [currentBackgroundIndex])
  
  // Rotate background on each dashboard visit
  useEffect(() => {
    if (location.pathname === '/') {
      // Get a random background index different from the current one
      let newIndex
      do {
        newIndex = Math.floor(Math.random() * backgroundImages.length)
      } while (newIndex === currentBackgroundRef.current && backgroundImages.length > 1)
      
      setCurrentBackgroundIndex(newIndex)
    }
  }, [location.pathname])

  const handleSignOut = async () => {
    try {
      const { error } = await signOut()
      if (error) {
        console.error('Sign out error:', error)
        // Still navigate to sign in page even if there's an error
        // to prevent users from being stuck in an authenticated state
      }
      // Navigate to sign-in page after successful sign out
      navigate('/auth/signin')
    } catch (err) {
      console.error('Sign out failed:', err)
      // Navigate to sign-in page even on error
      navigate('/auth/signin')
    }
  }

  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(${backgroundImages[currentBackgroundIndex]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat'
      }}
    >
      
      {/* Content wrapper */}
      <div className="relative z-10 min-h-screen">
      <Disclosure as="nav" className="bg-surface/98 backdrop-blur-md shadow-lg border-b border-neutral/20 relative z-[10000]">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex">
                  <div className="flex-shrink-0 flex items-center">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gradient-to-br from-secondary to-brown-sugar p-2 rounded-xl shadow-md">
                        <img src={CookieIconLogo} alt="The Cutter Keeper" className="w-16 h-10 rounded-md" />
                      </div>
                      <div>
                        <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                          The Cutter Keeper
                        </h1>
                        <p className="text-xs text-gray-700 font-medium">Dashboard</p>
                      </div>
                    </div>
                  </div>
                  <div className="hidden sm:ml-8 sm:flex sm:space-x-1">
                    {navigation.map((item) => {
                      const Icon = item.name === 'Inventory' && location.pathname === item.href ? HeartSolidIcon : item.icon
                      const isActive = location.pathname === item.href
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={classNames(
                            isActive
                              ? 'bg-gradient-to-br from-secondary/20 to-brown-sugar/20 text-primary border-b-2 border-secondary'
                              : 'text-gray-700 hover:bg-neutral/10 hover:text-primary border-b-2 border-transparent',
                            'inline-flex items-center px-4 py-2 rounded-t-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2'
                          )}
                        >
                          <Icon className="h-4 w-4 mr-2" />
                          {item.name}
                        </Link>
                      )
                    })}
                  </div>
                </div>
                <div className="hidden sm:ml-6 sm:flex sm:items-center">
                  <Menu as="div" className="ml-3 relative z-[10001]">
                    <div>
                      <Menu.Button className="bg-gradient-to-br from-neutral to-cambridge-blue rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary shadow-lg hover:shadow-xl transition-all duration-200">
                        <span className="sr-only">Open user menu</span>
                        <UserCircleIcon className="h-8 w-8 text-surface p-1" />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="origin-top-right fixed right-4 top-16 w-48 rounded-xl shadow-xl py-1 bg-surface border border-neutral/20 focus:outline-none z-[10001]">
                        <div className="px-4 py-3 text-sm border-b border-neutral/20 bg-gradient-to-r from-background/50 to-tea-green/50 rounded-t-xl">
                          <div className="font-medium text-primary">{profile?.display_name || user?.email}</div>
                          <div className="text-xs text-gray-700">{user?.email}</div>
                        </div>
                        
                        {/* Show Change Password for email/password users only */}
                        {user?.app_metadata?.provider === 'email' && (
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to="/auth/change-password"
                                className={classNames(
                                  active ? 'bg-gradient-to-r from-secondary/10 to-brown-sugar/10' : '',
                                  'flex w-full items-center px-4 py-2 text-sm text-primary hover:text-secondary transition-colors duration-200'
                                )}
                              >
                                <KeyIcon className="h-4 w-4 mr-2" />
                                Change Password
                              </Link>
                            )}
                          </Menu.Item>
                        )}
                        
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleSignOut}
                              className={classNames(
                                active ? 'bg-gradient-to-r from-secondary/10 to-brown-sugar/10' : '',
                                user?.app_metadata?.provider === 'email' 
                                  ? 'flex w-full items-center px-4 py-2 text-sm text-primary hover:text-secondary transition-colors duration-200 border-t border-neutral/20'
                                  : 'flex w-full items-center px-4 py-2 text-sm text-primary hover:text-secondary transition-colors duration-200 rounded-b-xl'
                              )}
                            >
                              <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                              Sign out
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
                <div className="-mr-2 flex items-center sm:hidden">
                  <Disclosure.Button className="bg-surface inline-flex items-center justify-center p-2 rounded-lg text-accent hover:text-primary hover:bg-neutral/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-secondary shadow-md transition-all duration-200">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="sm:hidden bg-surface/98 backdrop-blur-md border-t border-neutral/20">
              <div className="pt-2 pb-3 space-y-1 px-2">
                {navigation.map((item) => {
                  const Icon = item.name === 'Inventory' && location.pathname === item.href ? HeartSolidIcon : item.icon
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={classNames(
                        isActive
                          ? 'bg-gradient-to-r from-secondary/20 to-brown-sugar/20 border-secondary text-primary'
                          : 'border-transparent text-gray-700 hover:bg-neutral/10 hover:text-primary',
                        'block pl-3 pr-4 py-3 border-l-4 text-base font-medium rounded-r-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2'
                      )}
                    >
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 mr-3" />
                        {item.name}
                      </div>
                    </Link>
                  )
                })}
              </div>
              <div className="pt-4 pb-3 border-t border-neutral/20 bg-gradient-to-r from-background/30 to-tea-green/30">
                <div className="flex items-center px-4">
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-br from-neutral to-cambridge-blue rounded-full p-1">
                      <UserCircleIcon className="h-10 w-10 text-surface" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-primary">
                      {profile?.display_name || user?.email}
                    </div>
                    <div className="text-sm font-medium text-gray-700">{user?.email}</div>
                  </div>
                </div>
                <div className="mt-3 px-2">
                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center px-4 py-3 text-base font-medium text-gray-700 hover:text-secondary hover:bg-secondary/10 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

        <main className="py-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="bg-surface/95 backdrop-blur-md rounded-2xl shadow-xl border border-neutral/20 min-h-[calc(100vh-12rem)] p-6 lg:p-8">
              {children}
            </div>
          </div>
        </main>
        <VersionInfo position="bottom-right" />
      </div>
    </div>
  )
}